import { cn } from '@src/lib/utils';

import useCurrentLang from '@shared/hooks/useCurrentLang';
import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldMap as ProcessFieldMapType,
} from '@shared/types/process';

import { overlayTile } from './mapFunctions/mapFunctions.js';
import { MobileMapField } from './MobileMapField';
import OptionMap from './OptionMap.js';

import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ShadcnForm } from '@shadcn-features/core/index.js';
import { ShadcnFormInputProps } from '@shadcn-features/core/ShadcnForm/fields/ShadcnFormInput.js';
import { useWindowDimensions } from '@shadcn-features/core/util/useWindowDimensions';
import * as turf from '@turf/turf';
import L from 'leaflet';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FeatureGroup,
    LayersControl,
    MapContainer,
    TileLayer,
    useMap,
} from 'react-leaflet';
import './measure/leaflet.measure.js';
import './measure/leaflet.measure.scss';

import { toast } from 'sonner';

// Truncate value based on number of decimals
const _round = function (num: number, len: number) {
    return Math.round(num * Math.pow(10, len)) / Math.pow(10, len);
};
// Helper method to format LatLng object (x.xxxxxx, y.yyyyyy)
const strLatLng = function (latlng: L.LatLng) {
    return '(' + _round(latlng.lat, 6) + ', ' + _round(latlng.lng, 6) + ')';
};

const getPopupContent = function (layer: L.Layer) {
    if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
        return strLatLng(layer.getLatLng());
    } else if (layer instanceof L.Circle) {
        const center = layer.getLatLng(),
            radius = layer.getRadius();
        return (
            'Center: ' +
            strLatLng(center) +
            '<br />' +
            'Radius: ' +
            _round(radius, 2) +
            ' m'
        );
    } else if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs();

        // Проверяем, что первая и последняя точка одинаковы
        // @ts-ignore
        const firstPoint = latlngs[0][0];
        // @ts-ignore
        const lastPoint = latlngs[0][latlngs[0].length - 1];

        if (
            firstPoint.lat !== lastPoint.lat ||
            firstPoint.lng !== lastPoint.lng
        ) {
            // Закрываем полигон, добавляя первую точку в конец
            // @ts-ignore
            latlngs[0].push(firstPoint);
        }

        // Преобразуем LatLng в GeoJSON
        const polygon = turf.polygon([
            // @ts-ignore
            latlngs[0].map((latlng) => [latlng.lng, latlng.lat]),
        ]);
        const area = turf.area(polygon); // Площадь в квадратных метрах
        return 'Area: ' + _round(area, 2) + ' m²'; // Возвращаем площадь в квадратных метрах
    } else if (layer instanceof L.Polyline) {
        const latlngs = layer.getLatLngs();
        let distance = 0;
        if (latlngs.length < 2) {
            return 'Distance: N/A';
        } else {
            for (let i = 0; i < latlngs.length - 1; i++) {
                // @ts-ignore
                distance += latlngs[i].distanceTo(latlngs[i + 1]);
            }
            return 'Distance: ' + _round(distance, 2) + ' m';
        }
    }
    return null;
};

// Kazakh is not bundled by Geoman; we register it manually as an override on top of 'ru'.
// https://geoman.io/docs/leaflet/customize/language
const kkTranslations: L.PM.Translations = {
    tooltips: {
        placeMarker: 'Маркер қою үшін басыңыз',
        firstVertex: 'Бірінші нүктені қою үшін басыңыз',
        continueLine: 'Сызуды жалғастыру үшін басыңыз',
        finishLine: 'Аяқтау үшін кез келген бар маркерді басыңыз',
        finishPoly: 'Аяқтау үшін бірінші нүктені басыңыз',
        finishRect: 'Аяқтау үшін басыңыз',
        startCircle: 'Шеңбер орталығын қою үшін басыңыз',
        finishCircle: 'Радиусты белгілеу үшін басыңыз',
        placeCircleMarker: 'Дөңгелек маркер қою үшін басыңыз',
    },
    actions: {
        finish: 'Аяқтау',
        cancel: 'Бас тарту',
        removeLastVertex: 'Соңғы нүктені жою',
    },
    buttonTitles: {
        drawMarkerButton: 'Маркер қосу',
        drawPolyButton: 'Полигон салу',
        drawLineButton: 'Сызық салу',
        drawCircleButton: 'Шеңбер салу',
        drawRectButton: 'Тіктөртбұрыш салу',
        editButton: 'Қабатты өңдеу',
        dragButton: 'Қабатты жылжыту',
        cutButton: 'Қабатты кесу',
        deleteButton: 'Қабатты жою',
        drawCircleMarkerButton: 'Дөңгелек маркер қосу',
        rotateButton: 'Қабатты айналдыру',
    },
};

function applyPopupAndTooltip(layer: L.Layer) {
    const content = getPopupContent(layer);
    if (content === null) return;

    layer
        .bindPopup(content, {
            closeOnClick: false,
            autoClose: false,
        })
        .openPopup();
    layer.bindTooltip(content);
}

interface Props extends ShadcnFormInputProps {
    field: ProcessFieldBase & ProcessFieldMapType;
    value: string;
    onChange: (value: string) => void;
}

function GeomanDrawControls({
    featureGroupRef,
    onGeometryChange,
}: {
    featureGroupRef: L.FeatureGroup | undefined;
    onGeometryChange: () => void;
}) {
    const map = useMap();
    const lang = useCurrentLang();

    useEffect(() => {
        if (!featureGroupRef) return;

        map.pm.setGlobalOptions({ layerGroup: featureGroupRef });
        featureGroupRef.eachLayer((layer) => L.PM.reInitLayer(layer));

        // 'kk' is not in Geoman's SupportLocales; register as override on 'ru'.
        if (lang === 'kk') {
            map.pm.setLang('kk' as L.PM.SupportLocales, kkTranslations, 'ru');
        } else {
            map.pm.setLang(lang as L.PM.SupportLocales);
        }

        map.pm.addControls({
            position: 'topleft',
            drawMarker: true,
            drawPolyline: true,
            drawPolygon: true,
            drawRectangle: false,
            drawCircle: false,
            drawCircleMarker: false,
            drawText: false,
            editMode: true,
            removalMode: true,
        });

        const onCreate = (e: unknown) => {
            const layer = (e as { layer?: L.Layer }).layer;
            if (!layer) return;
            applyPopupAndTooltip(layer);
            onGeometryChange();
        };
        const onEdit = (e: unknown) => {
            const layer = (e as { layer?: L.Layer }).layer;
            if (!layer) return;
            applyPopupAndTooltip(layer);
            onGeometryChange();
        };
        const onRemove = () => onGeometryChange();

        map.on('pm:create', onCreate);
        map.on('pm:edit', onEdit);
        map.on('pm:remove', onRemove);

        return () => {
            map.off('pm:create', onCreate);
            map.off('pm:edit', onEdit);
            map.off('pm:remove', onRemove);
            map.pm.removeControls();
        };
    }, [featureGroupRef, lang, map, onGeometryChange]);

    return null;
}

function MapResizeObserver() {
    const map = useMap();
    useEffect(() => {
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(map.getContainer());
        return () => observer.disconnect();
    }, [map]);
    return null;
}

function InnerProcessFieldMapField({
    onChange,
    mobile = false,
    preview = false,
    ...props
}: Props & { mobile?: boolean; preview?: boolean }) {
    const { t } = useTranslation();

    // const { notification } = App.useApp();

    const [featureGroupRef, setFeatureGroupRef] = useState<L.FeatureGroup>();
    const handelChange = useCallback(() => {
        const data = featureGroupRef?.toGeoJSON();
        if (data) {
            const json = JSON.stringify(data);
            onChange(json);
        }
    }, [featureGroupRef, onChange]);

    return (
        <MapContainer
            scrollWheelZoom={false}
            dragging={!preview}
            touchZoom={!preview}
            doubleClickZoom={!preview}
            boxZoom={!preview}
            keyboard={!preview}
            zoomControl={!preview}
            className={cn(
                mobile &&
                    '[&_.leaflet-buttons-control-button]:size-11! [&_.leaflet-control-zoom_a]:size-11! [&_.leaflet-control-zoom_a]:leading-[44px]! [&_.leaflet-pm-action]:min-h-11 [&_.leaflet-pm-action]:leading-[44px]! [&_.leaflet-pm-action]:whitespace-nowrap [&_.leaflet-pm-actions-container]:top-full! [&_.leaflet-pm-actions-container]:left-0! [&_.leaflet-pm-actions-container]:w-[calc(100vw-24px)] [&_.leaflet-pm-actions-container]:whitespace-normal! [&_.leaflet-pm-toolbar]:flex [&_.leaflet-pm-toolbar.activeChild]:z-[801]! [&_.leaflet-pm-toolbar>.button-container]:static!',
                preview && '[&_.leaflet-control-layers]:hidden',
            )}
            style={{
                height: '100%',
                marginBottom: mobile ? 0 : 16,
                width: '100%',
            }}
        >
            {mobile && <MapResizeObserver />}
            <LayersControl collapsed={mobile} position='topright'>
                {overlayTile.map((el, index) => (
                    <LayersControl.BaseLayer
                        key={index}
                        checked={el.checked}
                        name={el.label}
                    >
                        {el.tile}
                    </LayersControl.BaseLayer>
                ))}
                <LayersControl.Overlay name={t('map:satellite')}>
                    <TileLayer
                        subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                        url='https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'
                    />
                </LayersControl.Overlay>
            </LayersControl>

            <FeatureGroup
                ref={(reactFGref) => {
                    if (!reactFGref || reactFGref === featureGroupRef) return;
                    try {
                        setFeatureGroupRef(reactFGref);
                        if (props.value) {
                            const getGeoJson = JSON.parse(props.value);
                            const leafletGeoJSON = new L.GeoJSON(
                                getGeoJson as GeoJSON.GeoJSON,
                            );
                            leafletGeoJSON.eachLayer((layer) => {
                                if (!preview) applyPopupAndTooltip(layer);

                                reactFGref.addLayer(layer);
                            });
                        }
                    } catch (error) {
                        console.error(error);
                        toast.error(t('translation:mapParseErrorMessage'), {
                            description: t(
                                'translation:mapParseErrorDescription',
                            ),
                        });
                    }
                }}
            >
                {!preview && !props.readOnly && !props.disabled && (
                    <GeomanDrawControls
                        featureGroupRef={featureGroupRef}
                        onGeometryChange={handelChange}
                    />
                )}
            </FeatureGroup>
            <OptionMap {...props} />
        </MapContainer>
    );
}

export const ProcessFieldMapField = ({ field, ...props }: Props) => {
    const { isMobile } = useWindowDimensions();
    const getLangText = useLangText();
    const { t } = useTranslation();
    const requiredRule = props.rules?.find((rule) => rule.required);
    const required = props.required || !!requiredRule;
    const requiredMessage =
        requiredRule?.message || t('translation:requiredField');
    return (
        <div
            style={{
                gridRow: '2/3',
                gridColumn: '1/3',
            }}
        >
            <ShadcnForm.Item
                name={props.name}
                label={props.label ?? getLangText(field.name)}
                required={required}
                readOnly={props.readOnly}
                tooltip={props.tooltip ?? getLangText(field.tooltip)}
                defaultValue={props.initialValue}
                rules={[
                    ...(props.rules ?? []),
                    {
                        required,
                        message: requiredMessage,
                        validator: (value) => {
                            if (!required || !value) return true;
                            try {
                                const geometry = (
                                    typeof value === 'string'
                                        ? JSON.parse(value)
                                        : value
                                ) as GeoJSON.GeoJSON;
                                const hasGeometry =
                                    geometry?.type === 'FeatureCollection'
                                        ? geometry.features.some(
                                              (feature) => !!feature.geometry,
                                          )
                                        : geometry?.type === 'Feature'
                                          ? !!geometry.geometry
                                          : !!geometry?.type;
                                return hasGeometry || requiredMessage;
                            } catch {
                                return requiredMessage;
                            }
                        },
                    },
                ]}
            >
                {({ value, onChange }) => (
                    <div
                        style={{
                            height: isMobile
                                ? undefined
                                : (field.options.minHeight || '400') + 'px',
                        }}
                    >
                        {isMobile ? (
                            <MobileMapField
                                title={props.label ?? getLangText(field.name)}
                                value={value as string}
                                onChange={onChange}
                                readOnly={props.readOnly}
                                disabled={props.disabled}
                                renderMap={(mapValue, onMapChange, preview) => (
                                    <InnerProcessFieldMapField
                                        key={preview ? mapValue : 'editor'}
                                        {...props}
                                        field={field}
                                        value={mapValue}
                                        onChange={onMapChange}
                                        mobile
                                        preview={preview}
                                    />
                                )}
                            />
                        ) : (
                            <InnerProcessFieldMapField
                                field={field}
                                {...props}
                                value={value as string}
                                onChange={onChange}
                            />
                        )}
                    </div>
                )}
            </ShadcnForm.Item>
        </div>
    );
};
